/**
 * Detailed Impact (v2) store
 * --------------------------
 * Owns the v2-artifact reads that power the "View detailed impact (beta)"
 * surface on `PublishPreview.vue`. Kept SEPARATE from
 * `usePublishPreviewStore` so:
 *   - Phase 4B is trivially revertable (delete the file, drop the import).
 *   - The legacy preview result blob flow is untouched.
 *   - State transitions for v2 (probed / available / loading / fallback)
 *     don't pollute the legacy store's status machine.
 *
 * Anti-flicker design
 * -------------------
 * The gate component renders nothing until `gateProbed === true`. Probe
 * results are cached per `simulationId` so navigating away and back, or
 * any other re-mount, never re-flashes a v2 button before re-evaluating.
 *
 * Structured logging
 * ------------------
 * Every non-`ok` probe outcome emits exactly one
 * `console.warn(JSON.stringify({...}))` so CloudWatch/Sentry consumers
 * can parse it. Auth throws (401/403) are surfaced via the existing
 * toast and are NOT logged here.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  tryGetPublishPreviewManifest,
  tryGetPublishPreviewSummary,
  tryGetPublishPreviewVinTrail,
  type PublishObservabilityManifest,
  type PublishObservabilitySummary,
  type PublishVinTrail,
} from '@/services/publishPreviewArtifactsService'

export type ProbeFailureReason = 'notFound' | 'route' | 'network' | 'http5xx'

export type SummaryStatus = 'idle' | 'loading' | 'ok' | 'unavailable'
export type VinTrailStatus =
  | 'idle'
  | 'invalid'
  | 'loading'
  | 'ok'
  | 'notFound'
  | 'unavailable'

type ProbeCacheEntry = {
  available: boolean
  manifest: PublishObservabilityManifest | null
}

const VIN_MIN_LENGTH = 1
const VIN_MAX_LENGTH = 64

export const useDetailedImpactStore = defineStore('detailedImpact', () => {
  const gateProbed = ref(false)
  const gateAvailable = ref(false)
  const manifest = ref<PublishObservabilityManifest | null>(null)
  const probedSimulationId = ref<string | null>(null)

  const dialogOpen = ref(false)

  const summary = ref<PublishObservabilitySummary | null>(null)
  const summaryStatus = ref<SummaryStatus>('idle')

  const vinTrail = ref<PublishVinTrail | null>(null)
  const vinTrailStatus = ref<VinTrailStatus>('idle')
  const vinTrailQuery = ref('')
  const vinTrailQueryEcho = ref('')

  /**
   * Pending VIN handoff for "open detailed impact for this row" flows
   * (Phase 4C). The dialog watches `pendingLookupNonce` (NOT
   * `pendingLookupVin`) so that clicking the same row repeatedly after
   * a completed lookup re-triggers the lookup. The duplicate-network
   * guard still lives in `lookupVin` (`vinTrailStatus === 'loading'`),
   * so rapid clicks during a single in-flight request collapse to one
   * actual fetch.
   *
   * Empty `pendingLookupVin` means "no pending row-action lookup". The
   * dialog must NOT overwrite the user-typed input when the pending VIN
   * is empty.
   */
  const pendingLookupVin = ref('')
  const pendingLookupNonce = ref(0)

  /**
   * Cache of probe outcomes keyed by simulationId. Prevents the gate
   * from re-flashing during re-mount or simulationId change-and-back.
   * In-memory only (cleared on full page reload, which is correct
   * because the manifest could have been written since).
   */
  const probeCache = new Map<string, ProbeCacheEntry>()

  const summaryAvailable = computed(() => summaryStatus.value === 'ok' && summary.value !== null)
  const vinTrailReady = computed(() => vinTrailStatus.value === 'ok' && vinTrail.value !== null)

  /**
   * Probe whether v2 artifacts exist for this simulation. Cached per
   * simulationId. Auth/programmer errors propagate to the caller (which
   * surfaces them via toast); expected v2-absence is silent for dealers
   * and emits a structured warn for ops.
   */
  async function probeForSimulation(params: {
    endpoint: string
    simulationId: string
    sessionToken: string
  }): Promise<void> {
    const cached = probeCache.get(params.simulationId)
    if (cached) {
      applyProbeOutcome(params.simulationId, cached.available, cached.manifest)
      return
    }

    // Reset visible state but keep `gateProbed` reflecting the previous
    // sim until we have a definitive answer for the new one. We DO toggle
    // `gateProbed` to false on a sim change so we don't render a stale
    // button against the new sim — the gate template guards both flags.
    if (probedSimulationId.value !== params.simulationId) {
      gateProbed.value = false
      gateAvailable.value = false
      manifest.value = null
    }

    const result = await tryGetPublishPreviewManifest({
      endpoint: params.endpoint,
      simulationId: params.simulationId,
      sessionToken: params.sessionToken,
    })

    if (result.status === 'ok') {
      probeCache.set(params.simulationId, { available: true, manifest: result.data })
      applyProbeOutcome(params.simulationId, true, result.data)
      return
    }

    // Map every non-ok outcome to a structured warn for ops visibility.
    const reason: ProbeFailureReason =
      result.status === 'notFound'
        ? 'notFound'
        : (result as { reason: ProbeFailureReason }).reason
    emitProbeWarn(params.simulationId, reason)

    probeCache.set(params.simulationId, { available: false, manifest: null })
    applyProbeOutcome(params.simulationId, false, null)
  }

  function applyProbeOutcome(
    simulationId: string,
    available: boolean,
    manifestPayload: PublishObservabilityManifest | null,
  ) {
    probedSimulationId.value = simulationId
    gateAvailable.value = available
    manifest.value = manifestPayload
    gateProbed.value = true
  }

  /**
   * When `probeForSimulation` throws, settle the gate as unavailable without
   * touching `probeCache` so a later probe can retry. Publish Preview has no
   * legacy fallback surface anymore.
   */
  function setV2GateUnavailableAfterProbeError(simulationId: string) {
    probedSimulationId.value = simulationId
    gateAvailable.value = false
    manifest.value = null
    gateProbed.value = true
  }

  function openDialog() {
    dialogOpen.value = true
  }

  function closeDialog() {
    dialogOpen.value = false
  }

  /**
   * Open the dialog and stage `vin` as a row-action lookup. The dialog
   * watches the bumped `pendingLookupNonce` and consumes
   * `pendingLookupVin` exactly once.
   *
   * The nonce is the source of truth for "this is a fresh row click",
   * NOT the VIN value, so clicking the same row again after a
   * completed lookup re-triggers it. VIN normalization is `.trim()`
   * only — uppercasing happens server-side.
   */
  function openForVin(vin: string) {
    const trimmed = String(vin || '').trim()
    pendingLookupVin.value = trimmed
    pendingLookupNonce.value = pendingLookupNonce.value + 1
    dialogOpen.value = true
  }

  /**
   * Atomic read-and-clear used by the dialog after it has handed the
   * pending VIN off to the lookup pipeline. Clearing here prevents a
   * later open of the dialog (e.g. via the gate button) from
   * accidentally re-triggering the previous row-action lookup.
   */
  function consumePendingLookup(): string {
    const vin = pendingLookupVin.value
    pendingLookupVin.value = ''
    return vin
  }

  /**
   * Load the per-area summary. Idempotent: if a `summary` is already
   * loaded for the current simulation, this is a no-op. Callers may
   * pass `force: true` to retry after a fallback message.
   */
  async function loadSummary(params: {
    endpoint: string
    simulationId: string
    sessionToken: string
    force?: boolean
  }): Promise<void> {
    if (summaryStatus.value === 'loading') return
    if (!params.force && summaryStatus.value === 'ok' && summary.value !== null) return

    summaryStatus.value = 'loading'
    const result = await tryGetPublishPreviewSummary({
      endpoint: params.endpoint,
      simulationId: params.simulationId,
      sessionToken: params.sessionToken,
    })

    if (result.status === 'ok') {
      summary.value = result.data
      summaryStatus.value = 'ok'
      return
    }

    // notFound + unavailable both collapse to "unavailable" on the
    // dialog. The user-facing copy is the same in both cases.
    summary.value = null
    summaryStatus.value = 'unavailable'
  }

  /**
   * Look up a single VIN. Validates and trims the input; refuses
   * concurrent submits while a request is in flight to honor the
   * "prevent duplicate requests while loading" requirement.
   */
  async function lookupVin(params: {
    endpoint: string
    simulationId: string
    sessionToken: string
    vin: string
  }): Promise<void> {
    if (vinTrailStatus.value === 'loading') return

    const trimmed = String(params.vin || '').trim()
    if (trimmed.length < VIN_MIN_LENGTH || trimmed.length > VIN_MAX_LENGTH) {
      vinTrail.value = null
      vinTrailStatus.value = 'invalid'
      vinTrailQueryEcho.value = trimmed
      return
    }

    vinTrailQuery.value = trimmed
    vinTrailQueryEcho.value = trimmed
    vinTrailStatus.value = 'loading'

    const result = await tryGetPublishPreviewVinTrail({
      endpoint: params.endpoint,
      simulationId: params.simulationId,
      sessionToken: params.sessionToken,
      vin: trimmed,
    })

    if (result.status === 'ok') {
      vinTrail.value = result.data
      vinTrailStatus.value = 'ok'
      return
    }
    if (result.status === 'notFound') {
      vinTrail.value = null
      vinTrailStatus.value = 'notFound'
      return
    }
    vinTrail.value = null
    vinTrailStatus.value = 'unavailable'
  }

  /**
   * Reset transient summary/VIN state without dropping the probe cache
   * (the cache deliberately survives navigations because the manifest
   * is immutable for a given simulationId).
   */
  function resetTransientState() {
    summary.value = null
    summaryStatus.value = 'idle'
    vinTrail.value = null
    vinTrailStatus.value = 'idle'
    vinTrailQuery.value = ''
    vinTrailQueryEcho.value = ''
    dialogOpen.value = false
    pendingLookupVin.value = ''
    // We deliberately do NOT reset `pendingLookupNonce` here. The nonce
    // is monotonically increasing within a session and serves as a
    // "fresh row click" signal; resetting it could cause the dialog
    // watcher to miss the next openForVin if the value happens to
    // match the prior nonce.
  }

  /**
   * Hard reset (account switch / logout). Clears the probe cache too.
   */
  function reset() {
    resetTransientState()
    gateProbed.value = false
    gateAvailable.value = false
    manifest.value = null
    probedSimulationId.value = null
    probeCache.clear()
    // Hard reset (account switch / logout) IS allowed to zero the
    // nonce because no consumer can hold a stale reference across a
    // full reset.
    pendingLookupNonce.value = 0
  }

  function emitProbeWarn(simulationId: string, reason: ProbeFailureReason) {
    try {
      console.warn(
        JSON.stringify({
          event: 'publishPreview.v2.probeFailed',
          simulationId,
          reason,
        }),
      )
    } catch {
      // Defensive: console.warn or JSON.stringify failure must never
      // bubble into the UI flow.
    }
  }

  return {
    gateProbed,
    gateAvailable,
    manifest,
    probedSimulationId,
    dialogOpen,
    summary,
    summaryStatus,
    summaryAvailable,
    vinTrail,
    vinTrailStatus,
    vinTrailQuery,
    vinTrailQueryEcho,
    vinTrailReady,
    pendingLookupVin,
    pendingLookupNonce,

    probeForSimulation,
    openDialog,
    closeDialog,
    openForVin,
    consumePendingLookup,
    loadSummary,
    lookupVin,
    resetTransientState,
    reset,
    setV2GateUnavailableAfterProbeError,
  }
})
